/**
 * AlertasBanner — banner rojo siempre visible arriba del expediente.
 * Muestra alergias críticas + advertencias clínicas extraídas de los
 * antecedentes y de la última consulta.
 */
import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { Warning as WarningIcon, LocalHospital as HospitalIcon } from '@mui/icons-material';

export default function AlertasBanner({ expediente, ultimaConsulta }) {
  const alergias = extraerAlergias(expediente);
  const problemasActivos = extraerProblemasActivos(expediente);
  const alertasConsulta = ultimaConsulta?.sugerencias_ia?.alertas || [];

  const hayAlertas = alergias.length > 0 || alertasConsulta.length > 0;
  if (!hayAlertas && problemasActivos.length === 0) return null;

  return (
    <Box sx={{
      borderRadius: 3,
      mb: 2,
      overflow: 'hidden',
      border: alergias.length > 0 ? '2px solid #ef4444' : '1px solid #f59e0b',
      background: alergias.length > 0
        ? 'linear-gradient(135deg, #fee2e2 0%, #fef3c7 100%)'
        : '#fef3c7',
    }}>
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
        <WarningIcon sx={{
          color: alergias.length > 0 ? '#b91c1c' : '#b45309',
          mt: 0.3, flexShrink: 0
        }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {alergias.length > 0 && (
            <Box sx={{ mb: 0.5 }}>
              <Typography sx={{ fontWeight: 800, color: '#991b1b', fontSize: 12, letterSpacing: 0.5 }}>
                ⚠ ALERGIAS CONOCIDAS
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {alergias.map((a, i) => (
                  <Chip
                    key={i}
                    label={a}
                    size="small"
                    icon={<HospitalIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      background: '#fff',
                      color: '#991b1b',
                      border: '1px solid #ef4444',
                      fontWeight: 700,
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {alertasConsulta.length > 0 && (
            <Box sx={{ mt: alergias.length > 0 ? 1 : 0 }}>
              <Typography sx={{ fontWeight: 700, color: '#92400e', fontSize: 11, letterSpacing: 0.5 }}>
                ALERTAS CLÍNICAS
              </Typography>
              {alertasConsulta.slice(0, 3).map((a, i) => (
                <Typography key={i} sx={{ fontSize: 12, color: '#78350f' }}>
                  • {typeof a === 'string' ? a : a.mensaje || JSON.stringify(a)}
                </Typography>
              ))}
            </Box>
          )}

          {problemasActivos.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ fontWeight: 700, color: '#0c4a6e', fontSize: 11, letterSpacing: 0.5 }}>
                PROBLEMAS CRÓNICOS ACTIVOS
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.3 }}>
                {problemasActivos.map((p, i) => (
                  <Chip
                    key={i}
                    label={p}
                    size="small"
                    sx={{
                      background: '#dbeafe',
                      color: '#1e3a8a',
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// Extrae alergias del expediente. Soporta 2 formatos:
// - legado: string en antecedentes_personales_patologicos.alergias
// - nuevo: array [{sustancia, reaccion}] en alergias
export function extraerAlergias(expediente) {
  if (!expediente) return [];
  const a = expediente.antecedentes_personales_patologicos || {};
  const crudas = a.alergias;
  if (!crudas) return [];
  if (typeof crudas === 'string') {
    return crudas
      .split(/[,;]/)
      .map(s => s.trim())
      .filter(Boolean);
  }
  if (Array.isArray(crudas)) {
    return crudas
      .map(x => typeof x === 'string' ? x : (x.sustancia + (x.reaccion ? ` (${x.reaccion})` : '')))
      .filter(Boolean);
  }
  return [];
}

// Extrae problemas crónicos activos desde antecedentes
export function extraerProblemasActivos(expediente) {
  if (!expediente) return [];
  const a = expediente.antecedentes_personales_patologicos || {};
  const cronicas = a.enfermedades_cronicas;
  if (!cronicas) return [];
  if (Array.isArray(cronicas)) {
    return cronicas.map(x => typeof x === 'string' ? x : x.nombre).filter(Boolean);
  }
  if (typeof cronicas === 'string') {
    return cronicas.split(/[,;]/).map(s => s.trim()).filter(Boolean);
  }
  return [];
}
